import querystring, { ParsedUrlQuery } from 'querystring'

import moment, { Moment } from 'moment-timezone'
import { ApolloError, useQuery } from '@apollo/client'
import { LocationSensorState } from 'react-use/lib/useLocation'
import { RangeValue } from 'rc-picker/lib/interface'
import {
    AdminJobRequestFiltersInputType,
    UrlFilterType,
} from '@OpsQuality/JobRequestList/types'
import {
    JOB_REQUEST_STATUS_FOR_LIST,
    JOB_REQUEST_STATE,
    JOB_REQUEST_INSPECTION_STATE,
    JOB_REQUEST_INSPECTION_LABELS,
} from '@OpsQuality/constants'

import jobRequestListQuery, { mpCountriesQuery } from './query'
import {
    getAdminJobRequestsQuality_adminJobRequests_items as JobRequestType,
    getAdminJobRequestsQuality_adminJobRequests as jobRequestListQueryType,
} from './__generated__/getAdminJobRequestsQuality'

export const DATE_FORMAT = 'MM/DD/YY H:mm A'

const getDefaultFromDate = () => {
    return moment().startOf('day')
}

const getDefaultToDate = () => {
    return moment().startOf('day').add(1, 'days')
}

export const RANGE_PICKER_RANGE_CRITERIA: Record<
    string,
    Exclude<RangeValue<moment.Moment>, null>
> = {
    Today: [moment().startOf('day'), moment().endOf('day')],
    'This Week': [
        moment().startOf('day'),
        moment().startOf('day').add(1, 'weeks'),
    ],
    'This Month': [
        moment().startOf('day'),
        moment().startOf('day').add(1, 'months'),
    ],
}

export const RANGES_ARRAY = Object.keys(RANGE_PICKER_RANGE_CRITERIA).map(
    range => {
        return {
            key: range,
            val: RANGE_PICKER_RANGE_CRITERIA[
                range as keyof typeof RANGE_PICKER_RANGE_CRITERIA
            ],
        }
    },
)

export const useJobRequestListData = (
    limit: number,
    filters: AdminJobRequestFiltersInputType,
    offset?: number,
    sort?: string,
): {
    data?: {
        adminJobRequests: jobRequestListQueryType
    }
    loading: boolean
    error?: ApolloError
} => {
    if (!filters.jobRequestStatus?.length) {
        filters.jobRequestStatus = [
            JOB_REQUEST_STATE.ACCEPTED,
            JOB_REQUEST_STATE.COMPLETED,
        ]
    }

    return useQuery(jobRequestListQuery, {
        variables: {
            limit,
            offset,
            sort,
            filters,
        },
        fetchPolicy: 'cache-and-network',
    })
}

export const useMPCountries = () => {
    const { data } = useQuery(mpCountriesQuery, {
        fetchPolicy: 'cache-first',
    })

    return data?.mpCountries?.map(
        ({ id, name }: { id: string; name: string }) => {
            return { label: name, value: id }
        },
    )
}

export function getUrlFilter(location: LocationSensorState) {
    const filters: any = {}

    if (location.search && querystring.parse(location.search)['?filters']) {
        const parsedSearch: ParsedUrlQuery = querystring.parse(location.search)

        const filterSearchString = parsedSearch['?filters'] as string

        filterSearchString.split('|').forEach(eachSearch => {
            const res = eachSearch.split('=')

            if (
                (res[0] === 'fromDate' || res[0] === 'toDate') &&
                Boolean(res[1])
            ) {
                filters[res[0]] = moment(res[1], 'MMDDYYYY')
            } else if (
                res[1] &&
                (res[0] === 'priorityTypes' ||
                    res[0] === 'platformProcessTypeIds' ||
                    res[0] === 'statuses' ||
                    res[0] === 'inspectionStatus' ||
                    res[0] === 'countryIds' ||
                    res[0] === 'mfgDriIds' ||
                    res[0] === 'payout' ||
                    res[0] === 'businessUnits')
            ) {
                const stringArray = res[1]
                    .split(',')
                    .filter(stringEle => stringEle !== '')

                filters[res[0]] = stringArray
            } else {
                filters[res[0] as keyof typeof filters] = res[1]
            }
        })
    }

    return filters
}

export function getFormattedFilters(urlFilters: UrlFilterType) {
    const {
        searchString,
        searchType,
        fromDate,
        toDate,
        range,
        statuses,
        priorityTypes,
        platformProcessTypeIds,
        inspectionStatus,
        mfgDriIds,
        payout,
        countryIds,
        businessUnits,
    } = urlFilters

    const filters: AdminJobRequestFiltersInputType = {}

    if (searchString && searchType === 'jobRequest') {
        filters.jobRequestNameLike = searchString

        return filters
    }

    if (searchString && searchType === 'salesOrder') {
        filters.salesOrderNameLike = searchString

        return filters
    }

    const rangeArray = RANGES_ARRAY.find(eachRange => eachRange.key === range)

    if (rangeArray) {
        filters.fromDate = rangeArray.val?.[0] || undefined
        filters.toDate = rangeArray.val?.[1] || undefined
    } else {
        if (fromDate === undefined) {
            filters.fromDate = getDefaultFromDate()
        } else if (fromDate) {
            filters.fromDate = fromDate
        }

        if (toDate === undefined) {
            filters.toDate = getDefaultToDate()
        } else if (toDate) {
            filters.toDate = toDate
        }
    }

    if (statuses && statuses.length) {
        filters.jobRequestStatus = Object.keys(
            JOB_REQUEST_STATUS_FOR_LIST,
        ).filter(
            label =>
                statuses.indexOf(
                    JOB_REQUEST_STATUS_FOR_LIST[
                        label as keyof typeof JOB_REQUEST_STATUS_FOR_LIST
                    ],
                ) > -1,
        )
    }
    if (priorityTypes && priorityTypes.length) {
        filters.priority = priorityTypes
    }

    if (businessUnits && businessUnits.length) {
        filters.businessUnits = businessUnits
    }

    if (platformProcessTypeIds && platformProcessTypeIds.length) {
        filters.processTypeIds = platformProcessTypeIds.map(typeId =>
            Number(typeId),
        )
    }

    if (inspectionStatus && inspectionStatus.length) {
        filters.inspectionStatus = inspectionStatus
    }
    if (mfgDriIds) {
        filters.mfgDriUserId = mfgDriIds.map(driId => Number(driId))
    }
    if (payout) {
        filters.payout = { from: Number(payout[0]), to: Number(payout[1]) }
    }
    if (countryIds && countryIds.length) {
        filters.countryIds = countryIds.map(id => Number(id))
    }

    return filters
}

export function getInspectionStatusTag(reportState: string) {
    if (reportState === JOB_REQUEST_INSPECTION_STATE.PENDING) {
        return {
            color: 'warning',
            title: JOB_REQUEST_INSPECTION_LABELS[reportState],
        }
    }
    if (reportState === JOB_REQUEST_INSPECTION_STATE.IN_PROGRESS) {
        return {
            color: 'blue',
            title: JOB_REQUEST_INSPECTION_LABELS[reportState],
        }
    }
    if (reportState === JOB_REQUEST_INSPECTION_STATE.COMPLETED) {
        return {
            color: '',
            title: JOB_REQUEST_INSPECTION_LABELS[reportState],
        }
    }

    return {
        color: '',
        title: 'N/A',
    }
}

export function getAllProcesses(jobRequest: JobRequestType | undefined) {
    if (!jobRequest || !jobRequest.linesWithWorkOrderMapping) {
        return []
    }

    const processNames = [...jobRequest.linesWithWorkOrderMapping]
        ?.sort((a, b) => {
            if (a.processType && b.processType) {
                return 0
            } else if (!a.processType) {
                return 1
            }
            return -1
        })

        .map(({ processType }) => processType && processType.toUpperCase())

    return new Set(processNames)
}

export const getDateRange = (
    fromDate: Moment,
    toDate: Moment,
    pendingFromDate?: Moment | null,
    pendingToDate?: Moment | null,
    range?: string,
) => {
    const rangeArray = RANGES_ARRAY.find(r => {
        return r.key === range
    })

    let from: Moment | undefined | null = getDefaultFromDate()
    let to: Moment | undefined | null = getDefaultToDate()

    if (pendingFromDate === null && !pendingToDate === null) {
        from = null
        to = null
    } else if (rangeArray) {
        from = rangeArray.val?.[0]
        to = rangeArray.val?.[1]
    } else {
        if (pendingFromDate !== undefined) {
            from = pendingFromDate
        } else if (fromDate !== undefined) {
            from = fromDate
        }

        if (pendingToDate !== undefined) {
            to = pendingToDate
        } else if (toDate !== undefined) {
            to = toDate
        }
    }

    const rangeValue: RangeValue<moment.Moment> | undefined =
        from && to ? [from, to] : undefined

    return rangeValue
}
